/**
 * PATH: packages/email-hub/src/feature/components/AddToCrmChip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-ADDTOCRMCHIP-001
 * PURPOSE: Compact, multi-type "file this sender" chip that replaces the
 *          old dead "Not in CRM" label in the reader's identity slot. One
 *          tap reveals two inline choices — file as a CRM Contact/Client
 *          OR as a Supplier — reusing the two write endpoints the
 *          SenderIdentityCard already drives.
 * WHY:     Operator (2026-06-16): *"what do you mean not in CRM, CRM etc
 *          are categories of database contact structure which should
 *          self-populate if anything… it can be multiple things like a
 *          supplier and so on"*. The label was a dead end; a sender can be
 *          a customer (Contact/Client) OR a vendor (Supplier), so the slot
 *          now lets the operator file it as the right thing in one tap.
 * FLOW (triage v5, 2026-07-07 "suggest one, search the rest"):
 *   ┌────────────────────┐  tap +  ┌──────────┐
 *   │ "+ Contact  ⋯"      │ ──────▶ │ POST +    │  (AI's best guess is
 *   │ (idle, suggested)   │         │ "In CRM ✓"│   ONE tap, no choosing)
 *   └────────┬───────────┘         └──────────┘
 *            │ tap ⋯
 *   ┌────────▼──────────────┐  pick  ┌──────────┐
 *   │ [Contact] [Supplier] ✕ │ ─────▶ │ POST + ✓ │  (full choice)
 *   └───────────────────────┘        └──────────┘
 * NOTE: inline-expand (no absolute popover) so it can't be clipped by the
 *       reader's overflow containers — same lesson as the allocate popover.
 * RELATED:
 *   - SenderIdentityCard (shouldOfferManualCrmFiling gate + the resolved/
 *     supplier badges this chip's success state mirrors)
 *   - organisation/.../sender_profile_views.py (create-sender-profile /
 *     create-sender-supplier endpoints)
 */

import { useState } from 'react'
import {
  Building2,
  CheckCircle2,
  Loader2,
  Plus,
  User,
  X,
} from 'lucide-react'

import { quotechJson } from '../../_internal/services/quotechApi'
import { cn } from '../../_internal/lib/utils'
import type { Email } from '../hooks/useEmails.types'

interface CreateProfileResponse {
  created: boolean
  contact_id: number | null
  contact_name: string | null
  client_id: number | null
  client_name: string | null
}

interface CreateSupplierResponse {
  created: boolean
  supplier_id: string | null
  supplier_name: string | null
  contact_email: string | null
}

export interface AddToCrmChipProps {
  email: Pick<Email, 'id' | 'from_name' | 'from_address' | 'company_label' | 'category'>
  /** Refetch the email detail so the resolved Contact/Client/Supplier
   *  comes back from the server and the identity slot upgrades to the
   *  green "Known sender" / "Supplier" badge on its own. */
  onFiled?: () => void
  className?: string
}

export type FiledKind = 'crm' | 'supplier'

/** Pure I → O: the AI's best guess for HOW to file this sender, from the
 *  email's classified category. AP-side mail (supplier/bill) suggests the
 *  supplier master; everything else (quote/contract/invoice-from-a-person
 *  and the CRM-relevant set that gates this chip) suggests a CRM contact.
 *  "Suggest one, search the rest" (triage v5): the suggestion is ONE tap;
 *  the other kind stays one ⋯ away, so a wrong guess costs a single click,
 *  while the old forced [Contact][Supplier] choice cost one on EVERY use. */
export function suggestFiledKind(category: string | null | undefined): FiledKind {
  const c = (category || '').toLowerCase()
  return c === 'supplier' || c === 'bill' ? 'supplier' : 'crm'
}

const CHIP_BASE =
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors'

export function AddToCrmChip({ email, onFiled, className }: AddToCrmChipProps) {
  const [phase, setPhase] = useState<'idle' | 'choosing' | 'busy'>('idle')
  const [done, setDone] = useState<{ kind: FiledKind; name: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const senderLabel =
    email.from_name || email.from_address?.split('@')[0] || 'sender'

  async function fileAs(kind: FiledKind) {
    if (phase === 'busy') return
    setPhase('busy')
    setError(null)
    try {
      if (kind === 'crm') {
        const { data } = await quotechJson<CreateProfileResponse>(
          `/api/emails/${email.id}/create-sender-profile/`,
          { method: 'POST', body: {} },
        )
        setDone({
          kind: 'crm',
          name: data?.client_name || data?.contact_name || senderLabel,
        })
      } else {
        const { data } = await quotechJson<CreateSupplierResponse>(
          `/api/emails/${email.id}/create-sender-supplier/`,
          { method: 'POST', body: {} },
        )
        setDone({
          kind: 'supplier',
          name: data?.supplier_name || email.company_label || senderLabel,
        })
      }
      onFiled?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not file sender')
      setPhase('choosing')
    }
  }

  if (done) {
    return (
      <span
        className={cn(
          CHIP_BASE,
          'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 max-w-[200px]',
          className,
        )}
        data-testid="add-to-crm-done"
      >
        <CheckCircle2 className="h-2.5 w-2.5 shrink-0" aria-hidden />
        <span className="truncate">
          {done.kind === 'crm' ? 'In CRM' : 'Supplier'}
          {done.name ? ` · ${done.name}` : ''}
        </span>
      </span>
    )
  }

  if (phase === 'busy') {
    return (
      <span className={cn(CHIP_BASE, 'text-muted-foreground', className)}>
        <Loader2 className="h-2.5 w-2.5 shrink-0 animate-spin" aria-hidden />
        Filing…
      </span>
    )
  }

  if (phase === 'choosing') {
    return (
      <span className={cn('inline-flex items-center gap-1', className)}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            void fileAs('crm')
          }}
          className={cn(
            CHIP_BASE,
            'border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-900/50 dark:text-amber-300 dark:hover:bg-amber-900/20',
          )}
          title="File as a CRM contact / client (customer)"
        >
          <User className="h-2.5 w-2.5 shrink-0" aria-hidden />
          Contact
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            void fileAs('supplier')
          }}
          className={cn(
            CHIP_BASE,
            'border border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-900/50 dark:text-indigo-300 dark:hover:bg-indigo-900/20',
          )}
          title="File as a supplier / vendor (accounts payable)"
        >
          <Building2 className="h-2.5 w-2.5 shrink-0" aria-hidden />
          Supplier
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setPhase('idle')
            setError(null)
          }}
          className="rounded-full p-0.5 text-muted-foreground hover:bg-accent"
          aria-label="Cancel"
          title="Cancel"
        >
          <X className="h-2.5 w-2.5" aria-hidden />
        </button>
        {error && (
          <span className="text-[9px] text-red-500" role="alert">
            {error}
          </span>
        )}
      </span>
    )
  }

  // Idle: the AI's best-guess kind is ONE tap; ⋯ expands the full choice.
  const suggested = suggestFiledKind(email.category)
  const SuggestedIcon = suggested === 'supplier' ? Building2 : User
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          void fileAs(suggested)
        }}
        className={cn(
          CHIP_BASE,
          'border border-dashed border-border text-muted-foreground',
          'hover:bg-accent/50 hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        )}
        title={
          suggested === 'supplier'
            ? 'One tap: file this sender as a Supplier (AI read this as AP-side mail)'
            : 'One tap: file this sender as a CRM contact / client'
        }
        data-testid="add-to-crm-chip"
        data-suggested-kind={suggested}
      >
        <Plus className="h-2.5 w-2.5 shrink-0" aria-hidden />
        <SuggestedIcon className="h-2.5 w-2.5 shrink-0" aria-hidden />
        {suggested === 'supplier' ? 'Supplier' : 'Contact'}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setPhase('choosing')
        }}
        className="rounded-full px-1 py-0.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground"
        title="File as something else (contact / supplier)"
        aria-label="More filing options"
        data-testid="add-to-crm-more"
      >
        ⋯
      </button>
    </span>
  )
}
